import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { FileText, Upload, X } from "lucide-react"
import { useUpdateIncident } from "@/hooks/useIncidents"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"

interface RespostaAdministracaoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ocorrencia: {
    id: string
    title: string
    status: string
  }
  acao: 'aprovar' | 'rejeitar'
}

const RespostaAdministracaoModal = ({
  open,
  onOpenChange,
  ocorrencia,
  acao
}: RespostaAdministracaoModalProps) => {
  const [resposta, setResposta] = useState("")
  const [arquivos, setArquivos] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  
  const updateIncident = useUpdateIncident()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setArquivos(prev => [...prev, ...files])
  }

  const removeFile = (index: number) => {
    setArquivos(prev => prev.filter((_, i) => i !== index))
  }

  const uploadFiles = async (files: File[]): Promise<string[]> => {
    const uploadPromises = files.map(async (file) => {
      const fileExt = file.name.split('.').pop()
      const fileName = `admin-response-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `admin-responses/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('incident-photos')
        .upload(filePath, file)

      if (uploadError) {
        console.error("Erro no upload:", uploadError)
        throw new Error(`Erro ao fazer upload de ${file.name}`)
      }

      const { data: { publicUrl } } = supabase.storage
        .from('incident-photos')
        .getPublicUrl(filePath)

      return publicUrl
    })

    return Promise.all(uploadPromises)
  }

  const handleSubmit = async () => {
    if (!resposta.trim()) {
      toast.error("Por favor, adicione uma resposta")
      return
    }

    try {
      setUploading(true)
      
      let attachmentUrls: string[] = []
      if (arquivos.length > 0) {
        attachmentUrls = await uploadFiles(arquivos)
      }

      const newStatus = acao === 'aprovar' ? 'approved_by_admin' : 'rejected_by_admin'
      
      await updateIncident.mutateAsync({
        id: ocorrencia.id,
        status: newStatus,
        admin_response: resposta,
        admin_attachments: attachmentUrls.length > 0 ? attachmentUrls : null,
        approved_by_admin_at: acao === 'aprovar' ? new Date().toISOString() : null
      })

      toast.success(`Ocorrência ${acao === 'aprovar' ? 'aprovada' : 'rejeitada'} com sucesso!`)
      onOpenChange(false)
      setResposta("")
      setArquivos([])
    } catch (error) {
      console.error("Erro ao processar resposta:", error)
      toast.error("Erro ao processar resposta")
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {acao === 'aprovar' ? 'Aprovar' : 'Rejeitar'} Ocorrência
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-sm text-gray-700 mb-2">
              {ocorrencia.title}
            </h4>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resposta">
              Resposta da Administração <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="resposta"
              placeholder={`Adicione sua resposta para ${acao === 'aprovar' ? 'aprovação' : 'rejeição'} desta ocorrência...`}
              value={resposta}
              onChange={(e) => setResposta(e.target.value)}
              rows={4}
            />
          </div>

          {acao === 'aprovar' && (
            <div className="space-y-2">
              <Label htmlFor="arquivos">
                Anexar Documentos (PDF, Imagens)
              </Label>
              <Input
                id="arquivos"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileChange}
              />
              
              {arquivos.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Arquivos selecionados:</p>
                  {arquivos.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{file.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={uploading || !resposta.trim()}
              variant={acao === 'aprovar' ? 'default' : 'destructive'}
            >
              {uploading ? (
                <>
                  <Upload className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                acao === 'aprovar' ? 'Aprovar' : 'Rejeitar'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RespostaAdministracaoModal